// Polices de Playbook (D-024), servies avec l'app : aucun appel à un tiers.
import '@fontsource/anton/400.css';
import '@fontsource-variable/inter';
import '@fontsource/jetbrains-mono/500.css';
import '@fontsource/jetbrains-mono/700.css';
import './styles.css';
import { supabase, supabaseConfigured } from './lib/supabase';
import { renderLogin } from './ui/login';
import { refreshBell, renderApp, resetApp } from './ui/app';
import { html, mount } from './ui/dom';
import { applyBrand, storedBrand } from './ui/theme';
import { renderPublic } from './ui/public';
import { renderSitePublic } from './ui/site-public';
import { sitePublic } from './lib/site-api';
import { renderPartnerReport } from './ui/partner-report';
import { renderStatsLink } from './ui/live-stats';
import { renderInviteLanding } from './ui/invite-landing';
import { renderDerogAnswer } from './ui/derogation-answer';
import { renderProspectOptout } from './ui/prospect-optout';
import { renderSignupCreate } from './ui/signup-create';
import { renderClubView, renderTeamCreate } from './ui/gratuite-pub';
import { clubHostSlug } from './domain/slug';
import { clubPublic } from './lib/api';
import { applyAppHead } from './ui/app-icon';
import { keepViewportAfterKeyboard } from './ui/keyboard';
import { watchAppUpdates } from './lib/app-update';
import { applyAppearance } from './ui/appearance';
import { bootSkin, saveSkin, showSkin } from './ui/skin-boot';
import { renderPartnerOffer } from './ui/apporteurs';
import { renderClubData } from './ui/club-data';
import { renderFollowStop } from './ui/suivre';
import { renderReceiptLink } from './ui/recus';
import { renderLienAnswer } from './ui/lien-reponse';
import { renderPerkScan } from './ui/perk-scan';
import { renderTeamPhone } from './ui/game-phone';
import { renderTablePublic, renderTableTake } from './ui/table-public';
import { renderSheetLink } from './ui/table-sheet';

const root = document.getElementById('app')!;
// Page publique d'un club (D-031) : /c/<adresse>, sans compte ni connexion. Sur le
// sous-domaine d'un club (D-032, <adresse>.<base>) : la page publique à la racine,
// l'espace membres en /app (une ancienne adresse #/… y est renvoyée).
const hostSlug = clubHostSlug(location.hostname, import.meta.env.VITE_CLUB_HOST_BASE as string | undefined);
const atRoot = location.pathname === '/' || location.pathname === '';
// L'app sur l'écran d'accueil (D-102, D-104) : manifeste et icône dès le chargement (Safari garde ceux-là).
applyAppHead(storedBrand());
keepViewportAfterKeyboard();
watchAppUpdates();
applyAppearance();   // Clair / Sombre / Automatique, choisi dans Moi
if (hostSlug && atRoot && location.hash.startsWith('#/')) location.replace(`/app${location.hash}`);
const pathPub = /^\/c\/([a-z0-9][a-z0-9-]{1,38}[a-z0-9])\/?$/i.exec(location.pathname);
// Le site du club (D-140) : ses pages sous l'adresse du club (/le-club, /equipes/u13-filles…), ou /c/<adresse>/… ; les
// adresses de l'app et des pages sans compte gardent leur sens.
const pathSite = pathPub || /^\/c\/[a-z0-9-]+\/t\//i.test(location.pathname) ? null : /^\/c\/([a-z0-9][a-z0-9-]{1,38}[a-z0-9])(\/[a-z0-9][a-z0-9/-]*)\/?$/i.exec(location.pathname);
const hostSitePath = hostSlug && !atRoot && !/^\/(app|assets|icons|[psidufrlvjmotewk]|partenaires|donnees|creer|c)(\/|$)/i.test(location.pathname)
  && /^\/[a-z0-9][a-z0-9/-]*\/?$/i.test(location.pathname) ? location.pathname : null;
const pub = pathPub ? pathPub[1]!.toLowerCase() : pathSite ? pathSite[1]!.toLowerCase() : hostSlug && hostSitePath ? hostSlug
  : hostSlug && atRoot && !location.hash.startsWith('#/') ? hostSlug : null;
const sitePath = pathSite ? pathSite[2]! : hostSitePath ?? '';
// Créer l'espace d'un club (D-088) : #/creer/<jeton>, le lien reçu par e-mail après une demande
// d'inscription sur la page de vente ; sans compte (connecté ou non).
const createToken = /^#\/creer\/([0-9a-f]{32,128})\/?$/.exec(location.hash)?.[1];
// L'équipe gratuite (D-166) : « Créer mon équipe » (#/equipe/<jeton>, le lien reçu par le coach) et « Votre club sur
// Arceau » (#/votre-club/<jeton>[/refuser], le lien envoyé à l'adresse officielle du club) ; sans compte.
const teamToken = /^#\/equipe\/([0-9a-f]{64})\/?$/.exec(location.hash)?.[1];
const clubView = /^#\/votre-club\/([0-9a-f]{64})(\/refuser)?\/?$/.exec(location.hash);
// Page partenaires du club (D-096), sans compte : /partenaires/<jeton> (le lien d'un apporteur),
// /c/<adresse>/partenaires, ou /partenaires sur le sous-domaine du club.
const offerToken = /^\/partenaires\/([0-9a-f]{32,128})\/?$/.exec(location.pathname)?.[1] ?? null;
const offerSlug = /^\/c\/([a-z0-9][a-z0-9-]{1,38}[a-z0-9])\/partenaires\/?$/i.exec(location.pathname)?.[1]?.toLowerCase()
  ?? (hostSlug && /^\/partenaires\/?$/.test(location.pathname) ? hostSlug : null);
// Mentions légales et données du club (D-103), sans compte : /c/<adresse>/donnees, /donnees sur le sous-domaine
// du club, ou <lien reçu>/donnees (invitation /i/, saisie des stats /s/, page partenaires d'un apporteur).
const dataSlug = /^\/c\/([a-z0-9][a-z0-9-]{1,38}[a-z0-9])\/donnees\/?$/i.exec(location.pathname)?.[1]?.toLowerCase()
  ?? (hostSlug && /^\/donnees\/?$/.test(location.pathname) ? hostSlug : null);
const dataVia = /^\/(i|s|partenaires)\/([0-9a-f]{32,128})\/donnees\/?$/.exec(location.pathname);
// Couleurs du dernier club, dès l'ouverture (connexion comprise) ; sur le sous-domaine
// d'un club, celles de ce club tant que personne ne s'y est connecté.
let hostBrand: Promise<void> = Promise.resolve();
if (!pub && !createToken && !teamToken && !clubView && !offerToken && !offerSlug && !dataSlug && !/^\/[psidufrlvjmotewk]\//.test(location.pathname) && !/^\/c\/[a-z0-9-]+\/t\//i.test(location.pathname)) {
  const stored = storedBrand();
  applyBrand(stored, false);
  if (hostSlug && (!stored || stored.id === `host:${hostSlug}`)) hostBrand = clubPublic(hostSlug).then(d => {
    if (d) applyBrand({ id: `host:${hostSlug}`, name: d.club.name, short_name: d.club.short_name, logo_url: d.club.logo, primary_color: d.club.color }, true);
  }).catch(() => { /* page publique non activée : couleurs Arceau */ });
}

// Espace d'un partenaire (D-033) : /p/<jeton>, lien privé, sans compte.
const partnerToken = /^\/p\/([0-9a-f]{32,128})\/?$/.exec(location.pathname)?.[1];
// Saisie des stats sans compte (D-057) : /s/<jeton>, le temps du match.
const statsToken = /^\/s\/([0-9a-f]{32,128})\/?$/.exec(location.pathname)?.[1];
// Lien d'invitation (D-075) : /i/<jeton>, sans compte ; relie le compte une fois connecté.
const inviteToken = /^\/i\/([0-9a-f]{32,128})\/?$/.exec(location.pathname)?.[1];
// Pré-demande de dérogation (D-083) : /d/<jeton>, l'autre club répond sans compte.
const derogToken = /^\/d\/([0-9a-f]{32,128})\/?$/.exec(location.pathname)?.[1];
// Prospection (D-086) : /u/<jeton>, « pas intéressé » ou « ne plus recevoir d'e-mails », sans compte.
const unsubToken = /^\/u\/([0-9a-f]{32,128})\/?$/.exec(location.pathname)?.[1];
// Suivre (D-110) : /f/<jeton>, « ne plus recevoir l'e-mail du soir des matchs », sans compte.
const followStopToken = /^\/f\/([0-9a-f]{32,128})\/?$/.exec(location.pathname)?.[1];
// Reçu fiscal (D-124) : /r/<jeton>, le lien envoyé par e-mail à une personne qui a donné sans compte.
const receiptToken = /^\/r\/([0-9a-f]{64})\/?$/.exec(location.pathname)?.[1];
// Partenaires (D-147) : /l/<jeton>, la page de réponse à une invitation (et « Ne plus recevoir ces messages »), sans compte.
const lienToken = /^\/l\/([0-9a-f]{64})\/?$/.exec(location.pathname)?.[1];
// Avantages validés par QR code (D-156) : /v/<jeton>, la page ouverte par l'appareil photo du partenaire (ou du bénévole de
// service), sans application ni compte ; reconnue par son compte ou son lien privé déjà ouvert sur ce téléphone.
const scanToken = /^\/v\/([0-9a-f]{32})\/?$/.exec(location.pathname)?.[1];
// Arceau Game (D-167) : /j/<jeton>, le téléphone d'une équipe ouvert par le QR de sa couleur sur la tablette ; sans compte,
// valable le temps de la partie.
const gameToken = /^\/j\/([0-9a-f]{32})\/?$/.exec(location.pathname)?.[1];
// La table de marque (D-176), sans compte : /m/<jeton> le suivi d'un match (« ?tv » : en grand), /o/<jeton> la feuille du
// club envoyée au coach adverse, /t/<jeton> le QR « Passer la main » scanné (ce téléphone devient la table).
const matchToken = /^\/m\/([0-9a-f]{32})\/?$/.exec(location.pathname)?.[1];
const sheetToken = /^\/o\/([0-9a-f]{64})\/?$/.exec(location.pathname)?.[1];
const takeToken = /^\/t\/([0-9a-f]{32})\/?$/.exec(location.pathname)?.[1];
// Le tournoi du club (D-177), sans compte : /t/<adresse> la page publique à l'adresse du club (ou /c/<club>/t/<adresse>),
// /e/<jeton> « Mon équipe » ouverte par le QR de l'équipe (capitaine ou coach), /w/<jeton> la TV de la salle (lien secret),
// /k/<jeton> le QR « Tenir la table » d'un terrain (ou « Passer la main »).
const tourPath = takeToken ? null : /^\/(?:c\/([a-z0-9][a-z0-9-]{1,38}[a-z0-9])\/)?t\/([a-z0-9][a-z0-9-]{1,38}[a-z0-9])(\/inscription)?\/?$/i.exec(location.pathname);
const tourClub = tourPath ? (tourPath[1] ?? hostSlug ?? null)?.toLowerCase() ?? null : null;
const tteamToken = /^\/e\/([0-9a-f]{32})\/?$/.exec(location.pathname)?.[1];
const ttvToken = /^\/w\/([0-9a-f]{32})\/?$/.exec(location.pathname)?.[1];
const tcourtToken = /^\/k\/([0-9a-f]{32})\/?$/.exec(location.pathname)?.[1];

if ((dataSlug || dataVia) && supabase && supabaseConfigured) {
  void renderClubData(root, dataSlug ? { slug: dataSlug } : { token: dataVia![2]! }, c => dataSlug
    ? { href: c.public ? (hostSlug ? '/' : `/c/${dataSlug}`) : hostSlug ? '/app' : '/', label: c.public ? 'Le club' : 'Espace membres' }
    : { href: `/${dataVia![1]}/${dataVia![2]}`, label: 'Retour' });
} else if (createToken && supabase && supabaseConfigured) {
  void renderSignupCreate(root, createToken);
} else if (teamToken && supabase && supabaseConfigured) {
  void renderTeamCreate(root, teamToken);
} else if (clubView && supabase && supabaseConfigured) {
  void renderClubView(root, clubView[1]!, !!clubView[2]);
} else if (partnerToken && supabase && supabaseConfigured) {
  void renderPartnerReport(root, partnerToken);
} else if (statsToken && supabase && supabaseConfigured) {
  void renderStatsLink(root, statsToken);
} else if (derogToken && supabase && supabaseConfigured) {
  void renderDerogAnswer(root, derogToken, new URLSearchParams(location.search).has('stop'));
} else if (unsubToken && supabase && supabaseConfigured) {
  void renderProspectOptout(root, unsubToken);
} else if (followStopToken && supabase && supabaseConfigured) {
  void renderFollowStop(root, followStopToken);
} else if (receiptToken && supabase && supabaseConfigured) {
  void renderReceiptLink(root, receiptToken);
} else if (gameToken && supabase && supabaseConfigured) {
  renderTeamPhone(root, gameToken);
} else if (matchToken && supabase && supabaseConfigured) {
  void renderTablePublic(root, matchToken);
} else if (takeToken && supabase && supabaseConfigured) {
  void renderTableTake(root, takeToken);
} else if (tourPath && tourClub && supabase && supabaseConfigured) {
  void import('./ui/tournoi-public').then(m => (tourPath[3] ? m.renderSignup(root, tourClub, tourPath[2]!.toLowerCase()) : m.renderTournamentPublic(root, tourClub, tourPath[2]!.toLowerCase())));
} else if (tteamToken && supabase && supabaseConfigured) {
  void import('./ui/tournoi-public').then(m => m.renderTeamPage(root, tteamToken));
} else if (ttvToken && supabase && supabaseConfigured) {
  void import('./ui/tournoi-public').then(m => m.renderTournamentTv(root, ttvToken));
} else if (tcourtToken && supabase && supabaseConfigured) {
  void import('./ui/tournoi-public').then(m => m.renderCourtPhone(root, tcourtToken));
} else if (sheetToken && supabase && supabaseConfigured) {
  void renderSheetLink(root, sheetToken);
} else if (scanToken && supabase && supabaseConfigured) {
  void renderPerkScan(root, scanToken, hostSlug ? '/app' : '/');
} else if (lienToken && supabase && supabaseConfigured) {
  void renderLienAnswer(root, lienToken, new URLSearchParams(location.search));
} else if ((offerToken || offerSlug) && supabase && supabaseConfigured) {
  void renderPartnerOffer(root, offerSlug, offerToken, !!hostSlug);
} else if (inviteToken && supabase && supabaseConfigured) {
  void renderInviteLanding(root, inviteToken, hostSlug ? '/app' : '/');
} else if (pub && supabase && supabaseConfigured) {
  // Le site du club (D-140) quand il est en ligne ; sinon la page gratuite, sans changement.
  void sitePublic(pub).catch(() => null).then(site => {
    const prefix = pathPub || pathSite ? `/c/${pub}` : '';
    if (site) return renderSitePublic(root, pub, site, sitePath, { prefix, members: hostSlug ? '/app' : '/' });
    if (sitePath) { location.replace(prefix || '/'); return; }
    // Racine du sous-domaine d'un club dont la page publique n'est pas en ligne (D-104) : l'espace membres.
    if (!pathPub) return clubPublic(pub).then(d => (d ? renderPublic(root, pub, '/app') : location.replace('/app')))
      .catch(() => renderPublic(root, pub, '/app'));
    return renderPublic(root, pub, hostSlug ? '/app' : '/');
  });
} else if (!supabase || !supabaseConfigured) {
  mount(root, html`
    <main class="login">
      <header class="login-brand"><div class="ball" aria-hidden="true"></div><h1>Arceau</h1></header>
      <p class="login-foot">Base non configurée — voir <code>.env.example</code>.</p>
    </main>`);
} else {
  // undefined = pas encore su ; null = déconnecté ; sinon l'identifiant du compte.
  let uid: string | null | undefined;

  supabase.auth.onAuthStateChange((_event, session) => {
    const next = session?.user.id ?? null;
    if (next === uid) return;            // rafraîchissement du jeton : rien à redessiner
    uid = next;
    resetApp();
    // Connexion aux couleurs du club (sous-domaine) : on attend sa marque, une fraction de seconde.
    // La télécommande d'Arceau Game (D-167, le QR du coach) : l'adresse est reprise une fois connecté.
    if (!next && /^#\/basket\/jeu\/coach\//.test(location.hash)) { try { sessionStorage.setItem('arceau.apres-connexion', location.hash); } catch { /* navigation privée */ } }
    if (next) { try { const back = sessionStorage.getItem('arceau.apres-connexion'); if (back) { sessionStorage.removeItem('arceau.apres-connexion'); if (/^#\/basket\/jeu\/coach\/[0-9a-f-]{36}$/.test(back)) history.replaceState(null, '', `${location.pathname}${back}`); } } catch { /* navigation privée */ } }
    if (!next) { saveSkin(null); showSkin(null, false); history.replaceState(null, '', location.pathname); void hostBrand.then(() => { if (uid === null) renderLogin(root); }); }
    else { bootSkin(storedBrand()?.id ?? null, next); void renderApp(root); }   // le skin porté, dès l'ouverture (D-149)
  });
  window.addEventListener('hashchange', () => { if (uid) void renderApp(root); });
  // Retour sur l'app (onglet, téléphone déverrouillé) : la cloche se met à jour.
  let hiddenAt = 0;
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') { hiddenAt = Date.now(); return; }
    if (!uid) return;
    // Plus de 10 minutes ailleurs : tout est relu (une app laissée ouverte ne montre pas d'anciennes données).
    if (hiddenAt && Date.now() - hiddenAt > 10 * 60_000) { resetApp(); void renderApp(root); }
    else void refreshBell(root);
  });
}
